import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Receipt, FileText, PieChart, ShoppingBag, TrendingDown, BookOpen, Filter } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;
const monthOptions = () => {
  const out = [{ label: 'All time', value: '' }];
  const seen = new Set();
  const now = new Date();
  const y = now.getFullYear(); const m = now.getMonth();
  for (let i = 0; i < 12; i++) {
    const d = new Date(y, m - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (seen.has(value)) continue; seen.add(value);
    out.push({ label: d.toLocaleString('en-IN', { month: 'long', year: 'numeric' }), value });
  }
  return out;
};

const TABS = [
  { id: 'gstr1',    label: 'GSTR-1',         icon: FileText },
  { id: 'gstr3b',   label: 'GSTR-3B',        icon: Receipt },
  { id: 'hsn',      label: 'HSN Summary',    icon: PieChart },
  { id: 'purchase', label: 'Purchase Report',icon: ShoppingBag },
  { id: 'tds',      label: 'TDS Report',     icon: TrendingDown },
  { id: 'ledger',   label: 'Tax Ledger',     icon: BookOpen },
];

const AdminTax = () => {
  const [tab, setTab] = useState('gstr1');
  const [franchises, setFranchises] = useState([]);
  const [franchiseId, setFranchiseId] = useState('');
  const [period, setPeriod] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const endpoint = () => {
    const m = { gstr1: 'gstr1', gstr3b: 'gstr3b', hsn: 'hsn-summary', purchase: 'purchase-report', tds: 'tds-report', ledger: 'tax-ledger' };
    return `${API}/gst/${m[tab]}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const p = new URLSearchParams();
      if (franchiseId) p.set('franchise_id', franchiseId);
      if (period) p.set('period', period);
      const { data } = await axios.get(`${endpoint()}?${p.toString()}`, authCfg());
      setData(data);
    } catch (e) { console.warn(e); setData(null); }
    setLoading(false);
  // eslint-disable-next-line
  }, [tab, franchiseId, period]);

  useEffect(() => { (async () => { try { const { data } = await axios.get(`${API}/franchises`, authCfg()); setFranchises(data || []); } catch {} })(); }, []);
  useEffect(() => { setData(null); }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Tax & GST Compliance">
      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-neutral-200 rounded-xl p-3">
        <span className="text-xs font-semibold text-neutral-500 inline-flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Scope:</span>
        <select data-testid="tax-franchise-filter" value={franchiseId} onChange={(e) => setFranchiseId(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white">
          <option value="">Consolidated (all franchises)</option>
          {franchises.map((f) => <option key={f.franchise_id} value={f.franchise_id}>{f.district}</option>)}
        </select>
        <span className="text-xs font-semibold text-neutral-500 ml-3">Period:</span>
        <select data-testid="tax-period-filter" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white">
          {monthOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`tax-tab-${id}`} onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${tab === id ? 'bg-[#D97706] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#D97706]'}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>}

      {!loading && data && tab === 'gstr1'    && <Gstr1View data={data} />}
      {!loading && data && tab === 'gstr3b'   && <Gstr3bView data={data} />}
      {!loading && data && tab === 'hsn'      && <HsnView data={data} />}
      {!loading && data && tab === 'purchase' && <PurchaseView data={data} />}
      {!loading && data && tab === 'tds'      && <TdsView data={data} />}
      {!loading && data && tab === 'ledger'   && <LedgerView data={data} />}
    </AdminLayout>
  );
};

const Kpi = ({ label, value, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold" style={{ color }}>{label}</p>
    <p className="font-extrabold text-lg mt-1" style={{ color }}>{value}</p>
  </div>
);
const Th = ({ children, className = '' }) => <th className={`px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase text-left ${className}`}>{children}</th>;

const Gstr1View = ({ data }) => {
  const rows = data.rows || [];
  const totals = data.totals || {};
  return (
    <div data-testid="gstr1-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Invoices" value={data.count || 0} color="#7B1FA2" bg="#F3E5F5" testid="gstr1-kpi-count" />
        <Kpi label="Taxable value" value={fmt(totals.taxable_value)} color="#2E7D32" testid="gstr1-kpi-taxable" />
        <Kpi label="CGST + SGST" value={fmt((totals.cgst || 0) + (totals.sgst || 0))} color="#D97706" testid="gstr1-kpi-tax" />
        <Kpi label="Invoice value" value={fmt(totals.invoice_value)} color="#1E88E5" testid="gstr1-kpi-inv" />
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr><Th>Invoice #</Th><Th>Date</Th><Th>Customer</Th><Th>HSN</Th><Th className="text-right">Taxable</Th><Th className="text-right">CGST</Th><Th className="text-right">SGST</Th><Th className="text-right">Invoice value</Th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => (
                <tr key={r.invoice_no + i} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2 text-[11px] font-mono">{r.invoice_no}</td>
                  <td className="px-3 py-2 text-[11px] text-neutral-600">{r.invoice_date}</td>
                  <td className="px-3 py-2 text-xs truncate max-w-xs">{r.customer_name}</td>
                  <td className="px-3 py-2 text-[11px] font-mono">{r.hsn_code}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmt(r.taxable_value)}</td>
                  <td className="px-3 py-2 text-right text-[11px] text-[#D97706]">{fmt(r.cgst)}</td>
                  <td className="px-3 py-2 text-right text-[11px] text-[#D97706]">{fmt(r.sgst)}</td>
                  <td className="px-3 py-2 text-right text-xs font-bold text-[#1E88E5]">{fmt(r.invoice_value)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-xs text-neutral-400">No invoices in the selected scope.</td></tr>}
            </tbody>
          </table>
        </div>
        {rows.length > 100 && <p className="px-4 py-2 text-[11px] text-neutral-500">Showing first 100 of {rows.length}</p>}
      </div>
    </div>
  );
};

const Gstr3bView = ({ data }) => {
  const os = data.outward_supplies || {};
  const itc = data.itc || {};
  const nl = data.net_liability || {};
  return (
    <div data-testid="gstr3b-view" className="grid md:grid-cols-3 gap-4">
      <SectionCard title="Outward supplies (Table 3.1)" tone="green" rows={[
        ['Taxable value', os.taxable_value || 0],
        ['CGST', os.cgst || 0],
        ['SGST', os.sgst || 0],
        ['Total tax', os.total_tax || 0],
      ]} testid="gstr3b-outward" />
      <SectionCard title="Input Tax Credit (Table 4)" tone="blue" rows={[
        ['CGST ITC', itc.cgst || 0],
        ['SGST ITC', itc.sgst || 0],
        ['Total ITC', itc.total || 0],
      ]} testid="gstr3b-itc" />
      <SectionCard title="Net liability (Table 6)" tone="orange" rows={[
        ['Net CGST', nl.cgst || 0],
        ['Net SGST', nl.sgst || 0],
        ['Total payable', nl.total || 0],
      ]} testid="gstr3b-liability" bold />
    </div>
  );
};

const SectionCard = ({ title, rows, tone, testid, bold }) => {
  const colors = { green: ['#2E7D32', '#E8F5E9'], blue: ['#1E88E5', '#DBEAFE'], orange: ['#D97706', '#FEF3C7'] };
  const [color, bg] = colors[tone] || ['#4B5563', '#F3F4F6'];
  return (
    <div data-testid={testid} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 font-bold text-sm" style={{ color, background: bg }}>{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i} className={`border-b border-neutral-100 ${bold && i === rows.length - 1 ? 'bg-neutral-50' : ''}`}>
              <td className={`px-4 py-2 text-xs ${bold && i === rows.length - 1 ? 'font-bold' : 'text-neutral-600'}`}>{k}</td>
              <td className={`px-4 py-2 text-right text-xs font-bold`} style={{ color }}>{fmt(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const HsnView = ({ data }) => {
  const rows = data.rows || [];
  const totals = data.totals || {};
  return (
    <div data-testid="hsn-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="HSN codes" value={rows.length} color="#7B1FA2" testid="hsn-kpi-count" />
        <Kpi label="Total taxable" value={fmt(totals.taxable_value)} color="#2E7D32" testid="hsn-kpi-taxable" />
        <Kpi label="Total CGST+SGST" value={fmt((totals.cgst || 0) + (totals.sgst || 0))} color="#D97706" testid="hsn-kpi-tax" />
        <Kpi label="Grand total" value={fmt(totals.total)} color="#1E88E5" testid="hsn-kpi-total" />
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr><Th>HSN</Th><Th>Description</Th><Th>UQC</Th><Th className="text-right">Qty</Th><Th className="text-right">Taxable</Th><Th className="text-right">CGST</Th><Th className="text-right">SGST</Th><Th className="text-right">Total</Th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.hsn_code}-${i}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-3 py-2 text-xs font-mono font-bold">{r.hsn_code}</td>
                <td className="px-3 py-2 text-xs">{r.description}</td>
                <td className="px-3 py-2 text-[11px]">{r.uqc}</td>
                <td className="px-3 py-2 text-right text-xs">{r.total_quantity}</td>
                <td className="px-3 py-2 text-right text-xs">{fmt(r.taxable_value)}</td>
                <td className="px-3 py-2 text-right text-[11px] text-[#D97706]">{fmt(r.cgst)}</td>
                <td className="px-3 py-2 text-right text-[11px] text-[#D97706]">{fmt(r.sgst)}</td>
                <td className="px-3 py-2 text-right text-xs font-bold">{fmt(r.total)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-xs text-neutral-400">No HSN data.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PurchaseView = ({ data }) => {
  const rows = data.rows || [];
  const totals = data.totals || {};
  return (
    <div data-testid="purchase-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Purchases" value={data.count || 0} color="#7B1FA2" testid="purchase-kpi-count" />
        <Kpi label="Invoice value" value={fmt(totals.invoice_value)} color="#DC2626" testid="purchase-kpi-inv" />
        <Kpi label="Taxable" value={fmt(totals.taxable_value)} color="#2E7D32" testid="purchase-kpi-taxable" />
        <Kpi label="ITC (CGST+SGST)" value={fmt((totals.cgst || 0) + (totals.sgst || 0))} color="#1E88E5" testid="purchase-kpi-itc" />
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr><Th>Date</Th><Th>Vendor</Th><Th>Category</Th><Th>HSN</Th><Th className="text-right">Invoice</Th><Th className="text-right">Taxable</Th><Th className="text-right">CGST</Th><Th className="text-right">SGST</Th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => (
                <tr key={`${r.id || 'row'}-${i}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2 text-[11px] text-neutral-600">{r.date}</td>
                  <td className="px-3 py-2 text-xs truncate max-w-xs">{r.vendor_name}</td>
                  <td className="px-3 py-2 text-[11px]">{r.category}</td>
                  <td className="px-3 py-2 text-[11px] font-mono">{r.hsn_code}</td>
                  <td className="px-3 py-2 text-right text-xs font-bold text-red-600">{fmt(r.invoice_value)}</td>
                  <td className="px-3 py-2 text-right text-xs">{fmt(r.taxable_value)}</td>
                  <td className="px-3 py-2 text-right text-[11px] text-[#D97706]">{fmt(r.cgst)}</td>
                  <td className="px-3 py-2 text-right text-[11px] text-[#D97706]">{fmt(r.sgst)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-xs text-neutral-400">No purchases recorded.</td></tr>}
            </tbody>
          </table>
        </div>
        {rows.length > 100 && <p className="px-4 py-2 text-[11px] text-neutral-500">Showing first 100 of {rows.length}</p>}
      </div>
    </div>
  );
};

const TdsView = ({ data }) => {
  const rows = data.rows || [];
  const bySection = data.by_section || [];
  const totals = data.totals || {};
  return (
    <div data-testid="tds-view">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Kpi label="Entries" value={rows.length} color="#7B1FA2" testid="tds-kpi-count" />
        <Kpi label="Base amount" value={fmt(totals.base)} color="#2E7D32" testid="tds-kpi-base" />
        <Kpi label="TDS deducted" value={fmt(totals.tds_deducted)} color="#D97706" testid="tds-kpi-tds" />
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden mb-4">
        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50 font-bold text-sm">Section-wise summary</div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr><Th>Section</Th><Th>Label</Th><Th className="text-right">Rate</Th><Th className="text-right">Count</Th><Th className="text-right">Base</Th><Th className="text-right">TDS</Th></tr>
          </thead>
          <tbody>
            {bySection.map((s) => (
              <tr key={s.section} className="border-b border-neutral-100">
                <td className="px-3 py-2 text-xs font-mono font-bold">{s.section}</td>
                <td className="px-3 py-2 text-xs">{s.label}</td>
                <td className="px-3 py-2 text-right text-xs">{s.rate}%</td>
                <td className="px-3 py-2 text-right text-xs">{s.count}</td>
                <td className="px-3 py-2 text-right text-xs">{fmt(s.base)}</td>
                <td className="px-3 py-2 text-right text-xs font-bold text-[#D97706]">{fmt(s.tds)}</td>
              </tr>
            ))}
            {bySection.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-xs text-neutral-400">No TDS records. Tag expenses with a tds_section field to populate.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LedgerView = ({ data }) => {
  const entries = data.entries || [];
  const totalsByAccount = data.totals_by_account || {};
  return (
    <div data-testid="tax-ledger-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {Object.entries(totalsByAccount).map(([k, v]) => (
          <Kpi key={k} label={k.replace(/_/g, ' ').toUpperCase()} value={fmt(v)} color="#D97706" testid={`ledger-total-${k}`} />
        ))}
        {Object.keys(totalsByAccount).length === 0 && <Kpi label="No entries" value="—" color="#6B7280" testid="ledger-total-none" />}
      </div>
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr><Th>Date</Th><Th>Account</Th><Th>Franchise</Th><Th>Ref</Th><Th className="text-right">Amount</Th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-1.5 text-[11px]">{(e.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-1.5 text-[11px] font-semibold">{e.account}</td>
                  <td className="px-3 py-1.5 text-[11px] text-neutral-600">{e.district || e.franchise_id}</td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-neutral-500">{e.ref_type}:{(e.ref_id || '').slice(0, 8)}</td>
                  <td className="px-3 py-1.5 text-right text-xs font-bold text-[#D97706]">{fmt(e.amount)}</td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-xs text-neutral-400">No tax entries in the selected scope.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTax;
